// MQTT WebSocket Hook for real-time updates
import { useEffect, useEffectEvent, useMemo, useRef, useState, useSyncExternalStore, useCallback } from 'react';
import mqtt from 'mqtt';
import { useAuthStore } from '../store/authStore.js';
// Default configuration from environment
// For MQTT WebSocket, use /mqtt path on same host (proxied through Traefik)
const getDefaultMqttWsUrl = () => {
    if (import.meta.env?.VITE_BMS_WS_URL) {
        return import.meta.env.VITE_BMS_WS_URL;
    }
    // The browser-facing proxy serves /mqtt on the page's origin. Preserve its
    // explicit port too; the broker's internal listeners are not public URLs.
    if (typeof window !== 'undefined') {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}/mqtt`;
    }
    return 'ws://localhost:8083/mqtt'; // SSR fallback
};
export const defaultMqttConfig = {
    wsUrl: getDefaultMqttWsUrl(),
    vendorPrefix: import.meta.env?.VITE_BMS_MQTT_PREFIX || 'corteh',
    siteCode: import.meta.env?.VITE_BMS_SITE_CODE,
    clientIdPrefix: 'bms-ui',
    reconnectInterval: 5000,
};
// =============================================================================
// Topic Helpers
// =============================================================================
export function buildProvisioningTopic(vendorPrefix, hwUid, suffix) {
    return `${vendorPrefix}/v1/prov/hw/${hwUid}/${suffix}`;
}
export function buildRuntimeTopic(vendorPrefix, siteCode, gwType, gwInstance, suffix) {
    return `${vendorPrefix}/v1/sites/${siteCode}/gws/${gwType}/${gwInstance}/${suffix}`;
}
/**
 * Exact channel topic for a channel whose gateway is known.
 *
 * Channel traffic hangs off the gateway prefix and carries no container
 * segment: the gateway identity plus `ch_code` is the whole address.
 * BMS-spec docs/mqtt/topic-structure.md §Runtime Gateway Topics.
 */
export function buildChannelTopic(vendorPrefix, siteCode, gwType, gwInstance, chCode, suffix) {
    return `${vendorPrefix}/v1/sites/${siteCode}/gws/${gwType}/${gwInstance}/ch/${chCode}/${suffix}`;
}
/**
 * Channel topic filter for a `ch_code` whose gateway the caller does not hold.
 *
 * The REST channel payloads name a channel by its code, not by its gateway's
 * MQTT identity, so the browser subscribes across gateways and lets the broker
 * pick. Edge generates `ch_code` as `{gateway_code}.{pa}.{channel_id}`, which is
 * unique across a whole site, so in practice this resolves to one channel.
 */
export function buildChannelCodeFilter(vendorPrefix, siteCode, chCode, suffix) {
    return `${vendorPrefix}/v1/sites/${siteCode}/gws/+/+/ch/${chCode}/${suffix}`;
}
/** Every channel on a site, for the dashboard overlays. */
export function buildSiteChannelStateFilter(vendorPrefix, siteCode) {
    return `${vendorPrefix}/v1/sites/${siteCode}/gws/+/+/ch/+/state`;
}
/** Extract `ch_code` from a channel topic. Returns null for anything else. */
export function chCodeFromChannelTopic(topic) {
    return /\/gws\/[^/]+\/[^/]+\/ch\/([^/]+)\/(?:state|event|cmd)$/.exec(topic)?.[1] ?? null;
}
// =============================================================================
// MQTT Context and Hook
// =============================================================================
// Singleton MQTT client
let mqttClient = null;
let mqttConfig = { ...defaultMqttConfig };
let lastBroker = null;
let unsubscribeAuth;
/**
 * Merge the keys a caller actually supplied into the module configuration.
 *
 * The configuration is module-level and shared by every hook, and only the
 * layout knows the site. A later caller without a site — the dashboard's own
 * `useMqttConnection()`, say — used to replace the whole object and so set
 * `siteCode` back to the (unset) environment default; every topic builder
 * that reads `mqttConfig.siteCode` then went null. An `undefined` key now
 * inherits whatever an earlier caller set.
 */
function mergeMqttConfig(config) {
    if (!config)
        return;
    let next = null;
    for (const [key, value] of Object.entries(config)) {
        if (value === undefined || mqttConfig[key] === value)
            continue;
        next ??= { ...mqttConfig };
        next[key] = value;
    }
    if (next)
        mqttConfig = next;
}
// Subscription registry
const subscriptions = new Map();
// MQTT wildcard pattern matching
function matchesMqttPattern(pattern, topic) {
    const patternParts = pattern.split('/');
    const topicParts = topic.split('/');
    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const topicPart = topicParts[i];
        if (patternPart === '#') {
            // Multi-level wildcard - matches rest of topic
            return true;
        }
        if (patternPart === '+') {
            // Single-level wildcard - matches any single level
            if (topicPart === undefined)
                return false;
            continue;
        }
        // Exact match required
        if (patternPart !== topicPart)
            return false;
    }
    // Pattern and topic must have same number of levels (unless pattern ends with #)
    return patternParts.length === topicParts.length;
}
function currentBroker() {
    const broker = useAuthStore.getState().broker;
    return broker && Date.parse(broker.expiresAt) > Date.now() ? broker : null;
}
function sameBroker(a, b) {
    return a?.username === b?.username && a?.password === b?.password && a?.expiresAt === b?.expiresAt;
}
/** The payload as a plain object, or null for a string, array or retained clear. */
function asRecord(data) {
    return data && typeof data === 'object' && !Array.isArray(data) ? data : null;
}
// The MQTT client is a module-level singleton, so a bare `client.unsubscribe`
// on unmount is destructive whenever another mounted hook still wants that
// topic. Concretely: GRMSDashboard and the GRMSRoomDetailDrawer nested inside
// it both subscribe to channel state, so closing the drawer unsubscribed the
// topic out from under the dashboard behind it. Ref-counting per topic makes
// only the last release unsubscribe.
const sharedTopicRefs = new Map();
const sharedRegistrations = new Set();
/**
 * JSON when the payload parses, the raw text otherwise. An empty payload — a
 * retained message being cleared — arrives as `''`.
 */
function parseMqttPayload(payload) {
    const text = payload.toString();
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
/**
 * Route one message to the handlers that registered for its topic.
 *
 * The payload is parsed at most once, lazily, and the same value is handed to
 * the legacy per-topic registry and to every shared handler. A handler is only
 * invoked for a topic one of its own filters matches; the dispatcher used to
 * call every handler for every message and leave each to re-parse and filter.
 */
function dispatchMessage(topic, payload, retained = false) {
    let parsed = false;
    let data;
    const read = () => {
        if (!parsed) {
            data = parseMqttPayload(payload);
            parsed = true;
        }
        return data;
    };
    for (const [pattern, callbacks] of subscriptions) {
        if (pattern === topic || matchesMqttPattern(pattern, topic)) {
            const value = read();
            callbacks.forEach((cb) => cb(value));
        }
    }
    for (const registration of sharedRegistrations) {
        if (!registration.filters.some((filter) => filter === topic || matchesMqttPattern(filter, topic)))
            continue;
        try {
            registration.handler(topic, read(), retained);
        }
        catch (err) {
            console.error('[MQTT] Live handler failed for', topic, err);
        }
    }
}
/** Every topic some hook still holds, from either registry. */
function heldTopics() {
    return Array.from(new Set([...sharedTopicRefs.keys(), ...subscriptions.keys()]));
}
// Connection-state listeners live here rather than on a client, so a client
// replaced after a token change keeps reporting to the hooks that watch it.
const connectionListeners = new Set();
function notifyConnection(client, event) {
    // A client ended for a token change still emits `close`; it no longer
    // speaks for the connection.
    if (client !== mqttClient)
        return;
    connectionListeners.forEach((listener) => listener(event));
}
function createClient(broker) {
    lastBroker = broker;
    const options = {
        clientId: `${mqttConfig.clientIdPrefix}-${Math.random().toString(16).slice(2, 10)}`,
        clean: true,
        username: broker.username,
        password: broker.password,
        reconnectPeriod: mqttConfig.reconnectInterval,
        connectTimeout: 30000,
        // Every (re)connect replays the held topics below, from both registries
        // and onto whichever client is current; mqtt.js's own replay would only
        // cover topics this client object subscribed and would double the
        // retained deliveries.
        resubscribe: false,
    };
    const client = mqtt.connect(mqttConfig.wsUrl, options);
    client.on('connect', () => {
        if (client !== mqttClient)
            return;
        console.log('[MQTT] Connected to', mqttConfig.wsUrl);
        const topics = heldTopics();
        if (topics.length > 0) {
            client.subscribe(topics, { qos: 0 }, (err) => {
                if (err)
                    console.error('[MQTT] Failed to resubscribe', err);
            });
        }
        notifyConnection(client, { type: 'connect' });
    });
    client.on('reconnect', () => notifyConnection(client, { type: 'reconnect' }));
    client.on('close', () => notifyConnection(client, { type: 'close' }));
    client.on('error', (err) => {
        console.error('[MQTT] Connection error:', err);
        notifyConnection(client, { type: 'error', error: err });
    });
    client.on('message', (topic, payload, packet) => {
        if (client === mqttClient)
            dispatchMessage(topic, payload, packet?.retain ?? false);
    });
    return client;
}
function getOrCreateClient() {
    unsubscribeAuth ??= useAuthStore.subscribe(() => { getOrCreateClient(); });
    const broker = currentBroker();
    if (mqttClient && !sameBroker(broker, lastBroker)) {
        const stale = mqttClient;
        mqttClient = null;
        stale.end(true);
        connectionListeners.forEach(listener => listener({ type: 'close' }));
    }
    lastBroker = broker;
    if (broker && !mqttClient) {
        mqttClient = createClient(broker);
        notifyConnection(mqttClient, { type: 'reconnect' });
    }
    return mqttClient;
}
/** Keep this public hook name for callers; credentials come from authStore. */
export function reconnectMqttWithNewToken() {
    getOrCreateClient();
}
/**
 * @internal Tests only: drop the client, both registries and the merged
 * configuration, so each test starts from a fresh module state.
 */
export function resetMqttForTests() {
    const client = mqttClient;
    mqttClient = null;
    client?.end(true);
    lastBroker = null;
    unsubscribeAuth?.();
    unsubscribeAuth = undefined;
    mqttConfig = { ...defaultMqttConfig };
    subscriptions.clear();
    sharedTopicRefs.clear();
    sharedRegistrations.clear();
    connectionListeners.clear();
}
// =============================================================================
// Shared topic subscriptions (ref-counted)
// =============================================================================
/**
 * Subscribe to `topics` on the shared client, invoking `handler` for every
 * message on a topic one of them matches, until the returned teardown runs.
 *
 * The handler is registered at once. The wire subscription is made now when
 * the client is connected and otherwise on its next `connect` — however long
 * that takes — so a page opened during a broker outage goes live when the
 * broker returns, not only after a remount.
 *
 * @returns teardown to return from the effect.
 */
export function subscribeShared(topics, handler) {
    const client = getOrCreateClient();
    const registration = { filters: topics, handler };
    sharedRegistrations.add(registration);
    for (const topic of topics) {
        const refs = (sharedTopicRefs.get(topic) ?? 0) + 1;
        sharedTopicRefs.set(topic, refs);
        if (refs === 1 && client?.connected) {
            client.subscribe(topic, { qos: 0 }, (err) => {
                if (err)
                    console.error('[MQTT] Failed to subscribe to', topic, err);
            });
        }
    }
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        sharedRegistrations.delete(registration);
        for (const topic of topics) {
            const refs = (sharedTopicRefs.get(topic) ?? 1) - 1;
            if (refs > 0) {
                sharedTopicRefs.set(topic, refs);
            }
            else {
                sharedTopicRefs.delete(topic);
                // The client may have been replaced since; the current one holds the
                // wire subscription. Not connected means nothing to undo (clean session).
                if (!subscriptions.has(topic) && mqttClient?.connected)
                    mqttClient.unsubscribe(topic);
            }
        }
    };
}
/**
 * Trailing debounce with a maximum wait.
 *
 * Each `schedule()` pushes the call `waitMs` out, but never further than
 * `maxWaitMs` after the first call of the burst — so a steady stream of
 * messages spaced closer than `waitMs` still flushes at least once per
 * `maxWaitMs` instead of starving.
 */
export function createTrailingDebounce(fn, waitMs, maxWaitMs) {
    let timer = null;
    let burstStartedAt = null;
    const flush = () => {
        timer = null;
        burstStartedAt = null;
        fn();
    };
    return {
        schedule: () => {
            const now = Date.now();
            burstStartedAt ??= now;
            if (timer)
                clearTimeout(timer);
            const untilMax = Math.max(0, burstStartedAt + maxWaitMs - now);
            timer = setTimeout(flush, Math.min(waitMs, untilMax));
        },
        cancel: () => {
            if (timer)
                clearTimeout(timer);
            timer = null;
            burstStartedAt = null;
        },
    };
}
/** `{vendor}/v1/sites/{site}/rooms/+/{suffix}` — one room-scoped UI topic across a site. */
export function buildSiteRoomsTopicFilter(siteCode, suffix) {
    return `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/rooms/+/${suffix}`;
}
/** `{vendor}/v1/sites/{site}/{suffix}` — one site-level UI topic, e.g. `access/lock-events`. */
export function buildSiteTopic(siteCode, suffix) {
    return `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/${suffix}`;
}
// =============================================================================
// useMqttConnection Hook
// =============================================================================
function connectionStateNow() {
    return { state: mqttClient?.connected ? 'connected' : currentBroker() ? 'connecting' : 'disconnected', error: null };
}
export function useMqttConnection(config) {
    // Merged during render, not in an effect: the layout renders before the
    // pages inside it, so a child's topic builders see the site on their first
    // render. Merging is idempotent and only ever adds the keys supplied.
    mergeMqttConfig(config);
    const [state, setState] = useState(connectionStateNow);
    useEffect(() => {
        const listener = (event) => {
            switch (event.type) {
                case 'connect':
                    setState({ state: 'connected', error: null });
                    break;
                case 'error':
                    setState({ state: 'error', error: event.error.message });
                    break;
                case 'close':
                    setState((prev) => prev.state === 'error' ? prev : { state: 'disconnected', error: null });
                    break;
                case 'reconnect':
                    setState({ state: 'connecting', error: null });
                    break;
            }
        };
        // Registered before the client is created, so its first `reconnect` reaches
        // this hook; the poll below catches anything between render and here.
        connectionListeners.add(listener);
        getOrCreateClient();
        // Store changes reconnect immediately; this also disconnects at credential expiry.
        const tokenCheckInterval = setInterval(() => {
            getOrCreateClient();
            setState(connectionStateNow());
        }, 2000);
        return () => {
            clearInterval(tokenCheckInterval);
            connectionListeners.delete(listener);
        };
    }, []);
    const connect = useCallback(() => {
        const client = getOrCreateClient();
        if (client && !client.connected)
            client.reconnect();
    }, []);
    const disconnect = useCallback(() => {
        if (mqttClient) {
            mqttClient.end();
        }
    }, []);
    return { ...state, connect, disconnect };
}
/** The vendor prefix the shared client is configured with (the site comes from the auth store). */
export function getMqttVendorPrefix() {
    return mqttConfig.vendorPrefix;
}
// `useMqttConnected` reads the shared client and re-reads it on every connection event.
function readConnected() {
    return !!mqttClient?.connected;
}
function subscribeConnected(onChange) {
    const listener = () => onChange();
    connectionListeners.add(listener);
    return () => {
        connectionListeners.delete(listener);
    };
}
/**
 * True while the shared client is connected. Unlike `useMqttConnection` it only
 * watches: it never creates the client, merges configuration or polls the token,
 * so a screen can show "live" or "polling" without owning the connection.
 */
export function useMqttConnected() {
    return useSyncExternalStore(subscribeConnected, readConnected, readConnected);
}
// =============================================================================
// useMqttSubscription Hook
// =============================================================================
export function useMqttSubscription(topic, options) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const callbackRef = useRef(null);
    const enabled = options?.enabled !== false && !!topic;
    useEffect(() => {
        if (!enabled || !topic) {
            return;
        }
        const client = getOrCreateClient();
        // Create callback
        callbackRef.current = (payload) => {
            setData(payload);
            setError(null);
        };
        // Add to subscription registry
        if (!subscriptions.has(topic)) {
            subscriptions.set(topic, new Set());
            // First subscriber: subscribe now if connected; otherwise the client's
            // `connect` handler subscribes every held topic.
            if (client?.connected) {
                client.subscribe(topic, { qos: 0 }, (err) => {
                    if (err) {
                        setError(err.message);
                    }
                });
            }
        }
        subscriptions.get(topic).add(callbackRef.current);
        return () => {
            if (callbackRef.current) {
                const callbacks = subscriptions.get(topic);
                if (callbacks) {
                    callbacks.delete(callbackRef.current);
                    if (callbacks.size === 0) {
                        // Last subscriber, unsubscribe from topic
                        subscriptions.delete(topic);
                        if (!sharedTopicRefs.has(topic) && mqttClient?.connected)
                            mqttClient.unsubscribe(topic);
                    }
                }
            }
        };
    }, [topic, enabled]);
    return { data, error };
}
export function useChannelValue(chCode, options) {
    // A channel is addressed by its code, whichever container owns it — which is
    // what makes a zone-contained lock's channel readable here at all.
    const topic = chCode && mqttConfig.siteCode
        ? buildChannelCodeFilter(mqttConfig.vendorPrefix, mqttConfig.siteCode, chCode, 'state')
        : null;
    // Firmware publishes property map directly: { switch: true, ts: "2026-..." }
    // Different properties may arrive as separate MQTT messages (e.g., "switch" then "status").
    // Merge them so later messages don't overwrite earlier properties.
    const { data, error } = useMqttSubscription(topic, options);
    const [merged, setMerged] = useState(null);
    // Adjusted while rendering: a new topic (navigating between rooms/channels)
    // resets the merged state, and new data merges into it.
    const [mergedFrom, setMergedFrom] = useState(null);
    if (!mergedFrom || topic !== mergedFrom.topic || data !== mergedFrom.data) {
        const previous = !mergedFrom || topic !== mergedFrom.topic ? null : merged;
        setMergedFrom({ topic, data });
        if (data != null) {
            setMerged(previous && typeof previous === 'object' && typeof data === 'object'
                ? { ...previous, ...data }
                : data);
        }
        else if (previous !== merged) {
            setMerged(null);
        }
    }
    return {
        value: merged,
        timestamp: merged?.ts ?? null,
        error,
    };
}
export function useChannelEvent(chCode, options) {
    const topic = chCode && mqttConfig.siteCode
        ? buildChannelCodeFilter(mqttConfig.vendorPrefix, mqttConfig.siteCode, chCode, 'event')
        : null;
    const { data, error } = useMqttSubscription(topic, options);
    return {
        event: data?.event ?? null,
        timestamp: data?.ts ?? null,
        error,
    };
}
export function useGatewayStatus(gwType, gwInstance, options) {
    const topic = gwType && gwInstance && mqttConfig.siteCode
        ? buildRuntimeTopic(mqttConfig.vendorPrefix, mqttConfig.siteCode, gwType, gwInstance, 'status')
        : null;
    const { data, error } = useMqttSubscription(topic, options);
    return {
        status: data?.status ?? null,
        identify: data?.identify,
        lastSeen: data?.timestamp ?? null,
        error,
    };
}
/**
 * Subscribe to all gateway status updates via MQTT wildcard.
 * Returns a map of gatewayCode -> latest status.
 *
 * This enables real-time updates for the gateways list without polling.
 * Requires a complete session and its broker credential.
 */
export function useAllGatewayStatuses(siteCode) {
    const [statuses, setStatuses] = useState(new Map());
    // Force re-render when Map contents change (Maps don't trigger React updates by reference)
    const [_version, setVersion] = useState(0);
    // Subscribed directly rather than through useMqttSubscription because the
    // gatewayCode has to be parsed out of the message topic, which that hook does
    // not surface. It used to subscribe both ways — the useMqttSubscription copy
    // fed an empty effect while doubling this client's subscriptions to the
    // wildcard, so the broker delivered every gateway status message twice.
    useEffect(() => {
        if (!siteCode)
            return;
        const topic = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/gws/+/+/status`;
        const handleMessage = (msgTopic, payload, retained) => {
            // Parse topic to extract gatewayCode: .../gws/{type}/{instance}/status
            const match = msgTopic.match(/\/gws\/([^/]+)\/([^/]+)\/status$/);
            if (!match) {
                return;
            }
            const [, _gwType, gwInstance] = match;
            // Per user requirement: Gateway ID should be just the instance name (e.g. "gw1")
            // The prefix (kanx-gw1) was a legacy convention
            const gatewayCode = gwInstance;
            const data = asRecord(payload);
            if (!data)
                return;
            setStatuses(prev => {
                const next = new Map(prev);
                const prevEntry = prev.get(gatewayCode);
                // Parse devices from heartbeat, or preserve previous if not present
                // A roster that is not an array, or an entry that is not an object, must not
                // cost the gateway its status: the status is the point of the message.
                const newDevices = (Array.isArray(data.devices) ? data.devices : undefined)?.filter(d => d && typeof d === 'object' && d.hw_uid).map(d => ({
                    hwUid: d.hw_uid,
                    status: d.status ?? 'unknown',
                    pa: d.pa,
                    model: d.model,
                    fwVersion: d.firmware_version ?? d.fw_version,
                    lastSeenS: d.last_seen_s, // Age in seconds since last KANX heartbeat
                }));
                // A message that names no state says nothing about presence: it must not turn
                // an online gateway offline (nor an offline one online).
                const reported = typeof data.state === 'string' ? data.state : typeof data.status === 'string' ? data.status : undefined;
                const status = (reported ?? prevEntry?.status ?? 'offline');
                const receivedAt = new Date().toISOString();
                next.set(gatewayCode, {
                    gatewayCode,
                    status,
                    identify: data.identify,
                    lastSeen: data.ts ?? receivedAt,
                    hwUid: data.hw_uid,
                    firmwareVersion: data.firmware_version,
                    uptimeS: data.uptime_s,
                    devices: newDevices ?? prevEntry?.devices, // Preserve previous devices if missing
                    offlineSince: status === 'online'
                        ? null
                        : prevEntry && prevEntry.status !== 'online'
                            ? prevEntry.offlineSince ?? null
                            // A last will arriving live is the drop itself. A retained one is an
                            // old fact whose `ts` is when the will was registered at CONNECT
                            // (provisioning.md §LWT), not when the gateway dropped, so the time
                            // is unknown.
                            : retained ? null : receivedAt,
                });
                return next;
            });
            setVersion(v => v + 1); // Trigger re-render
        };
        return subscribeShared([topic], handleMessage);
    }, [siteCode]);
    return statuses;
}
// =============================================================================
// useRoomLiveValues Hook - Watch live state for a known set of channels
// =============================================================================
/**
 * Subscribe to live state for a named set of channel codes.
 * Returns a map of chCode -> latest value object.
 *
 * Topic pattern: {vendor}/v1/sites/{site}/gws/+/+/ch/+/state
 *
 * The caller passes the codes because a channel topic carries no container
 * segment — a room is not on the wire, so the room's channel list (which the
 * caller already has from REST) is what says which codes belong to it. That is
 * also why this works unchanged for a channel an access zone owns.
 *
 * Use on Dashboard to overlay real-time values on top of REST-cached
 * currentValue.
 */
export function useRoomLiveValues(chCodes) {
    const [values, setValues] = useState(new Map());
    // Force re-render when Map contents change
    const [, setVersion] = useState(0);
    // Stable dependency: the identity of the array changes on every render.
    const codeKey = chCodes ? [...chCodes].sort().join(',') : '';
    useEffect(() => {
        if (!codeKey || !mqttConfig.siteCode)
            return;
        const wanted = new Set(codeKey.split(','));
        const topic = buildSiteChannelStateFilter(mqttConfig.vendorPrefix, mqttConfig.siteCode);
        const handleMessage = (msgTopic, payload) => {
            const chCode = chCodeFromChannelTopic(msgTopic);
            if (!chCode || !wanted.has(chCode))
                return;
            const data = asRecord(payload);
            if (!data)
                return;
            setValues((prev) => {
                const next = new Map(prev);
                next.set(chCode, data);
                return next;
            });
            setVersion((v) => v + 1);
        };
        return subscribeShared([topic], handleMessage);
    }, [codeKey]);
    return values;
}
// =============================================================================
// useDiscoveryEvents Hook - Watch for new gateway discoveries
// =============================================================================
export function useDiscoveryEvents(options) {
    // Subscribe to all provisioning status topics
    const topic = `${mqttConfig.vendorPrefix}/v1/prov/hw/+/status`;
    const { data, error } = useMqttSubscription(topic, options);
    const [eventId, setEventId] = useState(0);
    const lastDataRef = useRef(null);
    // Increment eventId on every new message (even if same hwUid)
    useEffect(() => {
        if (data && data !== lastDataRef.current) {
            lastDataRef.current = data;
            setEventId((id) => id + 1);
        }
    }, [data]);
    // One object per message, so a consumer that lists it as an effect
    // dependency does not re-run on every render.
    const hwUid = data?.hw_uid ?? null;
    const event = error ? null : data;
    return useMemo(() => ({
        hwUid,
        event,
        eventId, // Changes on every message, even if same hwUid
    }), [hwUid, event, eventId]);
}
/**
 * Subscribe to live OTA deployment progress updates via MQTT wildcard.
 * Returns a map of gatewayCode -> latest OTA progress.
 *
 * Edge backend publishes to: {vendor}/v1/sites/{site}/gws/+/+/ota/progress
 * whenever a deployment status changes (from handleProgress or handleOtaResult).
 */
export function useOtaDeploymentUpdates(siteCode, options) {
    const [updates] = useState(() => new Map());
    const [version, setVersion] = useState(0);
    const onUpdate = useEffectEvent((data) => options?.onUpdate?.(data));
    useEffect(() => {
        if (!siteCode)
            return;
        const topic = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/gws/+/+/ota/progress`;
        const handleMessage = (_msgTopic, payload) => {
            const wire = asRecord(payload);
            const key = wire?.gateway_code;
            if (!wire || !key)
                return;
            // Transform snake_case wire format to camelCase
            const data = {
                gatewayCode: wire.gateway_code,
                status: wire.status,
                progress: wire.progress,
                message: wire.message,
                ts: wire.ts,
            };
            updates.set(key, data);
            setVersion(v => v + 1);
            onUpdate(data);
        };
        return subscribeShared([topic], handleMessage);
    }, [siteCode, updates]);
    return { updates, version };
}
/**
 * Subscribe to Edge-published access card lifecycle events.
 * Edge publishes to: {vendor}/v1/sites/{site}/access/cards/events
 */
export function useCardLifecycleEvents(siteCode, queryClient, options) {
    const [lastEvent, setLastEvent] = useState(null);
    const [version, setVersion] = useState(0);
    const onEvent = useEffectEvent((event) => options?.onEvent?.(event));
    useEffect(() => {
        if (!siteCode)
            return;
        const topic = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/access/cards/events`;
        const handleMessage = (_msgTopic, payload) => {
            const wire = asRecord(payload);
            const cardUid = wire?.card_uid ?? wire?.cardUid;
            if (!wire || !wire.type || !cardUid)
                return;
            const event = {
                type: wire.type,
                cardUid,
                ts: wire.ts ?? wire.timestamp ?? new Date().toISOString(),
                details: wire.details ?? {},
            };
            setLastEvent(event);
            setVersion(v => v + 1);
            queryClient?.invalidateQueries({ queryKey: ['accessCards'] });
            onEvent(event);
        };
        return subscribeShared([topic], handleMessage);
    }, [siteCode, queryClient]);
    return { lastEvent, version };
}
// The three axes are closed value sets, and a broadcast is the one place an
// arbitrary string can enter the live-state map. Narrow on ingest so an
// unrecognised value is dropped rather than stored and rendered.
const OCCUPANCY_STATUS_VALUES = new Set(['vacant', 'occupied']);
const HOUSEKEEPING_STATUS_VALUES = new Set(['dirty', 'cleaning', 'to_inspect', 'ready']);
const SERVICE_STATE_VALUES = new Set(['in_service', 'out_of_order', 'out_of_service']);
function asOccupancyStatus(value) {
    return typeof value === 'string' && OCCUPANCY_STATUS_VALUES.has(value)
        ? value
        : undefined;
}
function asHousekeepingStatus(value) {
    return typeof value === 'string' && HOUSEKEEPING_STATUS_VALUES.has(value)
        ? value
        : undefined;
}
function asServiceState(value) {
    return typeof value === 'string' && SERVICE_STATE_VALUES.has(value)
        ? value
        : undefined;
}
/**
 * Fold one room-scoped broadcast into the live-state map.
 *
 * The wire is `snake_case` on every key (spec §12.1); `GRMSLiveRoomState` is a
 * camelCase in-memory cache. The two spellings are deliberately different, so
 * read every field off `data` by its SPECIFIED key and never by the cache
 * field's name — `data.occupancyStatus` is `undefined` on the wire and would
 * silently fall through to `prev` on every message.
 */
export function applyGRMSLiveRoomUpdate(states, topic, data, receivedAt = new Date().toISOString()) {
    const occupancyMatch = topic.match(/\/rooms\/([^/]+)\/occupancy$/);
    const flagsMatch = topic.match(/\/rooms\/([^/]+)\/flags$/);
    const housekeepingMatch = topic.match(/\/rooms\/([^/]+)\/housekeeping$/);
    const roomCode = occupancyMatch?.[1] ?? flagsMatch?.[1] ?? housekeepingMatch?.[1];
    if (!roomCode)
        return false;
    const prev = states.get(roomCode) ?? {};
    if (occupancyMatch) {
        // The occupancy topic carries the service overlay too (spec §12.1); the
        // two together decide whether the room reads sellable.
        states.set(roomCode, {
            ...prev,
            occupancyStatus: asOccupancyStatus(data.occupancy_status) ?? prev.occupancyStatus,
            serviceState: asServiceState(data.service_state) ?? prev.serviceState,
            guestName: typeof data.guest_name === 'string' || data.guest_name === null
                ? data.guest_name
                : prev.guestName,
            ts: data.ts ?? receivedAt,
        });
    }
    else if (flagsMatch) {
        // `/flags` is a SPARSE PATCH with two independent producers (spec §12.1):
        // the GRMS publisher owns `dnd`, `mur`, `presence` and the two
        // `*_clear_pending` keys; the lock-contact broadcast owns `lock_open` and
        // `door_left_open`.
        // Every key falls back to `prev` when absent, so a message from one
        // producer never clears the other's last known field. Replacing the cached
        // object outright — the obvious-looking simplification — is exactly the bug
        // this merge exists to prevent.
        states.set(roomCode, {
            ...prev,
            dndActive: typeof data.dnd === 'boolean' ? data.dnd : prev.dndActive,
            murActive: typeof data.mur === 'boolean' ? data.mur : prev.murActive,
            presenceDetected: typeof data.presence === 'boolean' ? data.presence : prev.presenceDetected,
            dndClearPending: typeof data.dnd_clear_pending === 'boolean'
                ? data.dnd_clear_pending
                : prev.dndClearPending,
            murClearPending: typeof data.mur_clear_pending === 'boolean'
                ? data.mur_clear_pending
                : prev.murClearPending,
            // `lock_open` is tri-state: the aggregate is null when no contact is
            // linked or the readings are incomplete (§11.1). An explicit null must
            // therefore be merged as "unknown", while an omitted key still falls
            // back to `prev` like every other sparse-patch member.
            lockOpen: 'lock_open' in data
                ? (typeof data.lock_open === 'boolean' || data.lock_open === null
                    ? data.lock_open
                    : prev.lockOpen)
                : prev.lockOpen,
            doorLeftOpen: typeof data.door_left_open === 'boolean' ? data.door_left_open : prev.doorLeftOpen,
            ts: data.ts ?? receivedAt,
        });
    }
    else if (housekeepingMatch) {
        // Housekeeping axis only — this topic no longer republishes occupancy.
        states.set(roomCode, {
            ...prev,
            housekeepingStatus: asHousekeepingStatus(data.housekeeping_status) ?? prev.housekeepingStatus,
            housekeepingAttendant: typeof data.attendant === 'string' || data.attendant === null
                ? data.attendant
                : prev.housekeepingAttendant,
            ts: data.ts ?? receivedAt,
        });
    }
    return true;
}
/**
 * Subscribe to GRMS room state updates via MQTT wildcard.
 * Returns a Map of roomCode → latest GRMS state.
 *
 * Listens to:
 *   - corteh/v1/sites/{site}/rooms/+/occupancy  (occupancy status changes)
 *   - corteh/v1/sites/{site}/rooms/+/flags      (DND/MUR/presence changes)
 *   - corteh/v1/sites/{site}/rooms/+/housekeeping (cleaning status/attendant changes)
 */
export function useGRMSLiveState(siteCode, onInvalidate) {
    // A room code is only unique inside its site. Another site starts from an
    // empty overlay map, so a late/old-site value can never decorate the new
    // site's REST row.
    const [live, setLive] = useState(() => ({ siteCode, states: new Map() }));
    const [version, setVersion] = useState(0);
    if (live.siteCode !== siteCode) {
        setLive({ siteCode, states: new Map() });
        setVersion(v => v + 1);
    }
    const { states } = live;
    useEffect(() => {
        if (!siteCode)
            return;
        const base = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/rooms/+`;
        const topics = [`${base}/occupancy`, `${base}/flags`, `${base}/housekeeping`];
        const handleMessage = (msgTopic, payload) => {
            const data = asRecord(payload);
            if (data && applyGRMSLiveRoomUpdate(states, msgTopic, data)) {
                setVersion(v => v + 1);
                onInvalidate?.();
            }
        };
        return subscribeShared(topics, handleMessage);
    }, [siteCode, states, onInvalidate]);
    return { states, version };
}
export function applyAlarmQueryLiveUpdate(existing, event, retainResolved) {
    const alarms = existing ?? [];
    if (event.action === 'resolved') {
        if (!retainResolved)
            return alarms.filter(alarm => alarm.id !== event.id);
        return alarms.map(alarm => alarm.id === event.id
            ? { ...alarm, status: 'resolved', resolvedAt: event.ts, acknowledgedBy: event.acknowledgedBy ?? alarm.acknowledgedBy }
            : alarm);
    }
    const status = (event.action === 'acknowledged' ? 'acknowledged' : 'active');
    const existingAlarm = alarms.find(alarm => alarm.id === event.id);
    const updated = {
        id: event.id,
        roomCode: event.roomCode,
        alarmType: event.alarmType,
        severity: event.severity,
        message: event.message,
        status,
        acknowledgedBy: event.acknowledgedBy !== undefined
            ? event.acknowledgedBy
            : existingAlarm?.acknowledgedBy ?? null,
        acknowledgedAt: event.action === 'acknowledged'
            ? event.ts
            : existingAlarm?.acknowledgedAt ?? null,
        resolvedAt: null,
        metadata: existingAlarm?.metadata ?? {},
        createdAt: existingAlarm?.createdAt ?? event.ts,
    };
    return existingAlarm
        ? alarms.map(alarm => alarm.id === event.id ? updated : alarm)
        : [...alarms, updated];
}
export function applyRoomAlarmLiveUpdate(alarms, topic, event) {
    const match = topic.match(/\/rooms\/([^/]+)\/alarm$/);
    if (!match)
        return false;
    const roomCode = match[1];
    const existing = alarms.get(roomCode) ?? [];
    if (event.action === 'resolved') {
        alarms.set(roomCode, existing.filter(alarm => alarm.id !== event.id));
    }
    else if (event.action === 'acknowledged') {
        alarms.set(roomCode, existing.map(alarm => alarm.id === event.id ? { ...alarm, status: 'acknowledged' } : alarm));
    }
    else if (!existing.some(alarm => alarm.id === event.id)) {
        alarms.set(roomCode, [...existing, event]);
    }
    return true;
}
/**
 * Subscribe to alarm events via MQTT wildcard.
 * Returns a Map of roomCode → latest alarm events and a version counter.
 *
 * Listens to: corteh/v1/sites/{site}/rooms/+/alarm
 */
export function useRoomAlarmsLive(siteCode, queryClient) {
    const [alarms] = useState(() => new Map());
    const [version, setVersion] = useState(0);
    useEffect(() => {
        if (!siteCode)
            return;
        const alarmTopic = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/rooms/+/alarm`;
        const handleMessage = (msgTopic, payload) => {
            const data = asRecord(payload);
            if (!data || !applyRoomAlarmLiveUpdate(alarms, msgTopic, data))
                return;
            if (queryClient) {
                queryClient.setQueryData(['alarms', siteCode], current => applyAlarmQueryLiveUpdate(current, data, false));
                queryClient.setQueryData(['alarms', siteCode, data.roomCode], current => applyAlarmQueryLiveUpdate(current, data, true));
            }
            setVersion(v => v + 1);
        };
        return subscribeShared([alarmTopic], handleMessage);
    }, [siteCode, alarms, queryClient]);
    return { alarms, version };
}
// =============================================================================
// Lock Events Live (invalidates React Query cache on MQTT event)
// =============================================================================
/**
 * Subscribe to real-time lock event notifications via MQTT WebSocket.
 * On each event, invalidates lock-related React Query cache keys so all
 * visible lock queries refetch automatically. Call once per page that
 * shows lock data — do NOT embed inside individual data hooks.
 */
export function useLockEventsLive(siteCode, queryClient) {
    useEffect(() => {
        if (!siteCode || !queryClient)
            return;
        const topic = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/access/lock-events`;
        const handleMessage = () => {
            // Invalidate all lock-related query keys — React Query refetches only active ones
            queryClient.invalidateQueries({ queryKey: ['locks'] });
            queryClient.invalidateQueries({ queryKey: ['lockDetail'] });
            queryClient.invalidateQueries({ queryKey: ['lockEvents'] });
            queryClient.invalidateQueries({ queryKey: ['grms', 'roomEvents'] });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        };
        return subscribeShared([topic], handleMessage);
    }, [siteCode, queryClient]);
}
// =============================================================================
// useRoomEventsLive Hook - Real-time room event updates via MQTT
// =============================================================================
/**
 * Subscribe to real-time room event notifications via MQTT WebSocket.
 * Listens to: {vendor}/v1/sites/{site}/rooms/{roomCode}/events
 *
 * On each event, invalidates the room events React Query cache so
 * the LogTab refetches instantly. Also invalidates occupancy summary,
 * room history (for the Graphs tab), and related queries.
 */
export function useRoomEventsLive(siteCode, roomCode, queryClient) {
    useEffect(() => {
        if (!siteCode || !roomCode || !queryClient)
            return;
        const topic = `${mqttConfig.vendorPrefix}/v1/sites/${siteCode}/rooms/${roomCode}/events`;
        const handleMessage = () => {
            // Invalidate room events query so legacy consumers refetch, and unified events
            // so the room Log Tab stays current during the Phase 4 migration. The
            // summaries follow `/occupancy` and `/housekeeping` (useGrmsSummariesLive).
            queryClient.invalidateQueries({ queryKey: ['grms', 'roomEvents', siteCode, roomCode] });
            queryClient.invalidateQueries({ queryKey: ['grms', 'roomHistory', siteCode, roomCode] });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        };
        return subscribeShared([topic], handleMessage);
    }, [siteCode, roomCode, queryClient]);
}
// =============================================================================
// useRoomStateLive Hook - Patch room-list rows from the retained /state overlay
// =============================================================================
/** The room-list key the overlay patches; `queryKeys.rooms` in useApi.ts. */
function roomListKey(siteCode) {
    return ['rooms', siteCode];
}
function sameReading(a, b) {
    if (Object.is(a, b))
        return true;
    return typeof a === 'object' && typeof b === 'object' && a !== null && b !== null
        && JSON.stringify(a) === JSON.stringify(b);
}
/**
 * Spread one `/state` message onto the matching room-list row.
 *
 * `/state` carries the list row's own camelCase keys (GRMS §12.1), so the
 * patch is a spread; the row's identity stays its own whatever the payload
 * says. Returns `rows` itself when nothing moved, so an unchanged retained
 * replay re-renders no card.
 */
export function applyRoomStatePatch(rows, roomCode, state) {
    if (!rows)
        return rows;
    const index = rows.findIndex((row) => row.roomCode === roomCode);
    if (index < 0)
        return rows;
    const row = rows[index];
    if (Object.keys(state).every((key) => sameReading(row[key], state[key])))
        return rows;
    const next = rows.slice();
    next[index] = { ...rows[index], ...state, id: rows[index].id, roomCode: rows[index].roomCode };
    return next;
}
/**
 * Keep the room list's readings live from `rooms/+/state` (retained, QoS 0).
 *
 * Each message patches its row of the `['rooms', site]` query in place with
 * `setQueryData`, so a channel report costs one ~220 B message instead of a
 * room-list refetch; the 30 s revalidation and the reconnect, resume and
 * site-switch refetches stay (`useRoomListRefresh`, GRMS §11.1). An empty
 * payload is Edge clearing the retained value (room deleted, last channel
 * unmapped): there is nothing to patch, and the next list read drops the row.
 *
 * A REST snapshot in flight when a message arrives was computed before it, and
 * landing would overwrite the newer reading (§11.1: "MUST NOT overwrite newer
 * observations with an older response"). So every message received after a
 * list read started is applied again once that read lands.
 */
export function useRoomStateLive(siteCode, queryClient) {
    useEffect(() => {
        if (!siteCode || !queryClient)
            return;
        const key = roomListKey(siteCode);
        const isListQuery = (queryKey) => queryKey.length === 2 && queryKey[0] === key[0] && queryKey[1] === key[1];
        // Latest message per room and when it arrived.
        const latest = new Map();
        let readStartedAt = null;
        const patch = (roomCode, state) => {
            queryClient.setQueryData(key, (rows) => applyRoomStatePatch(rows, roomCode, state));
        };
        const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
            if (event.type !== 'updated' || !isListQuery(event.query.queryKey))
                return;
            if (event.action.type === 'fetch') {
                readStartedAt ??= Date.now();
            }
            else if (event.action.type === 'success' && !event.action.manual) {
                const since = readStartedAt;
                readStartedAt = null;
                if (since === null)
                    return;
                // Outside the cache's own notify pass, which is still dispatching.
                queueMicrotask(() => {
                    for (const [roomCode, entry] of latest) {
                        if (entry.at >= since)
                            patch(roomCode, entry.state);
                    }
                });
            }
            else if (event.action.type === 'error') {
                readStartedAt = null;
            }
        });
        const handleMessage = (topic, payload) => {
            const roomCode = /\/rooms\/([^/]+)\/state$/.exec(topic)?.[1];
            if (!roomCode)
                return;
            const state = asRecord(payload);
            if (!state) {
                latest.delete(roomCode);
                return;
            }
            latest.set(roomCode, { state, at: Date.now() });
            patch(roomCode, state);
        };
        const unsubscribe = subscribeShared([buildSiteRoomsTopicFilter(siteCode, 'state')], handleMessage);
        return () => {
            unsubscribe();
            unsubscribeCache();
        };
    }, [siteCode, queryClient]);
}
// =============================================================================
// useChannelStatesLive Hook - Invalidate room queries on channel state changes
// =============================================================================
/** Trailing quiet period for channel-state invalidation. */
export const CHANNEL_STATE_DEBOUNCE_MS = 250;
/** A burst still invalidates at least this often, however dense it is. */
export const CHANNEL_STATE_MAX_WAIT_MS = 1000;
/**
 * Subscribe to all channel state updates across all rooms via MQTT wildcard
 * and, debounced, invalidate the per-room queries that read channel values
 * (`['rooms', site, roomCode, …]` — room detail and its children).
 *
 * The room **list** is not invalidated here any more: its readings are patched
 * from `/state` (useRoomStateLive). A caller that does want the list refetched
 * passes `onInvalidate`.
 *
 * Debounce: 250 ms trailing, at most 1 s — batches gateway bursts, and a
 * stream of messages closer than 250 ms apart can no longer starve it.
 */
export function useChannelStatesLive(siteCode, queryClient, onInvalidate) {
    useEffect(() => {
        if (!siteCode || !queryClient)
            return;
        const topic = buildSiteChannelStateFilter(mqttConfig.vendorPrefix, siteCode);
        const debounce = createTrailingDebounce(() => {
            if (onInvalidate)
                onInvalidate();
            else
                void queryClient.invalidateQueries({
                    queryKey: roomListKey(siteCode),
                    predicate: (query) => query.queryKey.length > 2,
                });
        }, CHANNEL_STATE_DEBOUNCE_MS, CHANNEL_STATE_MAX_WAIT_MS);
        const unsubscribe = subscribeShared([topic], () => debounce.schedule());
        return () => {
            debounce.cancel();
            unsubscribe();
        };
    }, [siteCode, queryClient, onInvalidate]);
}
//# sourceMappingURL=useMqtt.js.map